import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';
import { PencilSquareIcon, TrashIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

const CATEGORIAS = ['Writing', 'Listening', 'Reading'];

const preguntaBase = () => ({
  texto: '', tipo_pregunta: 'multiple',
  opciones: ['', '', '', ''],
  respuesta_correcta: '',
});

const formBase = () => ({
  titulo: '', descripcion: '', categoria: 'Writing',
  duracion_minutos: '', subtipo: 'examen', subtipo_eval: 'test',
  link_audio: '', texto_lectura: '',
  preguntas: [preguntaBase()],
});

export default function Plantillas({ tipo, nivel }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const tema = getTema(tipo);
  const esProfesor = ['profesor', 'admin'].includes(usuario?.rol);

  const [plantillas, setPlantillas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const [modal, setModal] = useState(null); // null | 'crear' | 'editar'
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(formBase());
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState('');
  const [paso, setPaso] = useState(1);

  // Modal plantilla aleatoria
  const [modalAleatorio, setModalAleatorio] = useState(false);
  const [evalsDisponibles, setEvalsDisponibles] = useState([]);
  const [evalsSeleccionadas, setEvalsSeleccionadas] = useState(new Set());
  const [numPreguntasAleatorio, setNumPreguntasAleatorio] = useState('');
  const [tituloAleatorio, setTituloAleatorio] = useState('');
  const [creandoAleatorio, setCreandoAleatorio] = useState(false);
  const [errorAleatorio, setErrorAleatorio] = useState('');
  const [cargandoEvals, setCargandoEvals] = useState(false);

  useEffect(() => {
    if (!esProfesor) { navigate('/seleccion'); return; }
    cargar();
  }, []);

  const cargar = async () => {
    try {
      setCargando(true);
      const res = await axios.get(`${API_URL}/plantillas`);
      setPlantillas(res.data);
    } catch { setError('Error al cargar plantillas'); }
    finally { setCargando(false); }
  };

  const abrirCrear = () => {
    setForm(formBase());
    setEditandoId(null);
    setFormError('');
    setPaso(1);
    setModal('crear');
  };

  const abrirEditar = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/plantillas/${id}`);
      const p = res.data;
      setForm({
        titulo: p.titulo || '',
        descripcion: p.descripcion || '',
        categoria: p.categoria || 'Writing',
        duracion_minutos: p.duracion_minutos || '',
        subtipo: p.subtipo || 'examen',
        subtipo_eval: p.subtipo_eval || 'test',
        link_audio: p.link_audio || '',
        texto_lectura: p.texto_lectura || '',
        preguntas: p.preguntas?.length > 0 ? p.preguntas : [preguntaBase()],
      });
      setEditandoId(id);
      setFormError('');
      setPaso(1);
      setModal('editar');
    } catch { setError('Error al cargar la plantilla'); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    try {
      await axios.delete(`${API_URL}/plantillas/${id}`);
      setPlantillas(prev => prev.filter(p => p.id !== id));
    } catch { setError('Error al eliminar la plantilla'); }
  };

  const guardar = async () => {
    if (!form.titulo.trim()) { setFormError('El título es requerido'); return; }
    const esRedaccion = form.subtipo_eval === 'redaccion';
    if (!esRedaccion && form.preguntas.some(p => !p.texto.trim())) {
      setFormError('Todas las preguntas deben tener texto'); return;
    }
    setGuardando(true);
    setFormError('');
    try {
      if (editandoId) {
        await axios.put(`${API_URL}/plantillas/${editandoId}`, form);
      } else {
        await axios.post(`${API_URL}/plantillas`, form);
      }
      setModal(null);
      cargar();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const usarComoExamen = async (p) => {
    try {
      const res = await axios.get(`${API_URL}/plantillas/${p.id}`);
      navigate(`/${tipo}/${nivel}/crear-evaluacion`, { state: { plantilla: res.data } });
    } catch {
      navigate(`/${tipo}/${nivel}/crear-evaluacion`, { state: { plantilla: p } });
    }
  };

  const usarComoActividad = async (p) => {
    try {
      const res = await axios.get(`${API_URL}/plantillas/${p.id}`);
      navigate(`/${tipo}/${nivel}/crear-actividad`, { state: { plantilla: res.data } });
    } catch {
      navigate(`/${tipo}/${nivel}/crear-actividad`, { state: { plantilla: p } });
    }
  };

  const abrirModalAleatorio = async () => {
    setErrorAleatorio('');
    setEvalsSeleccionadas(new Set());
    setNumPreguntasAleatorio('');
    setTituloAleatorio(`Plantilla aleatoria — ${new Date().toLocaleDateString('es-ES')}`);
    setModalAleatorio(true);
    setCargandoEvals(true);
    try {
      const res = await axios.get(`${API_URL}/evaluaciones/todas`);
      setEvalsDisponibles(res.data);
    } catch { setErrorAleatorio('Error al cargar evaluaciones'); }
    setCargandoEvals(false);
  };

  const toggleEval = (id) => setEvalsSeleccionadas(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGrupo = (ids, todos) => setEvalsSeleccionadas(prev => {
    const next = new Set(prev);
    if (todos) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    return next;
  });

  const crearAleatoria = async () => {
    if (!tituloAleatorio.trim()) { setErrorAleatorio('El título es requerido'); return; }
    if (evalsSeleccionadas.size === 0) { setErrorAleatorio('Selecciona al menos una evaluación'); return; }
    setCreandoAleatorio(true);
    setErrorAleatorio('');
    try {
      await axios.post(`${API_URL}/plantillas/aleatoria`, {
        titulo: tituloAleatorio,
        evaluacion_ids: [...evalsSeleccionadas],
        num_preguntas: numPreguntasAleatorio ? parseInt(numPreguntasAleatorio) : null,
      });
      setModalAleatorio(false);
      cargar();
    } catch (e) { setErrorAleatorio(e.response?.data?.error || 'Error al crear'); }
    setCreandoAleatorio(false);
  };

  const addPregunta = () => setForm(f => ({ ...f, preguntas: [...f.preguntas, preguntaBase()] }));
  const removePregunta = (i) => setForm(f => ({ ...f, preguntas: f.preguntas.filter((_, idx) => idx !== i) }));
  const updatePregunta = (i, campo, val) => setForm(f => ({
    ...f,
    preguntas: f.preguntas.map((p, idx) => idx === i ? { ...p, [campo]: val } : p),
  }));
  const updateOpcion = (pi, oi, val) => setForm(f => ({
    ...f,
    preguntas: f.preguntas.map((p, idx) => idx === pi
      ? { ...p, opciones: p.opciones.map((o, oidx) => oidx === oi ? val : o) }
      : p),
  }));

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '5px' };

  const esRedaccionForm = form.subtipo_eval === 'redaccion';

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content">
          <div className="fade-in">
            <div className="section-header">
              <div>
                <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>📋 Plantillas de Exámenes</h2>
                <p style={{ color: tema.muted, fontSize: '14px' }}>Guarda exámenes como plantillas y úsalos cuando quieras</p>
              </div>
              <div className="section-header-actions">
                <button onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                  style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '10px 18px', borderRadius: '50px', fontSize: '14px', cursor: 'pointer' }}>
                  ← Volver
                </button>
                <button onClick={abrirModalAleatorio}
                  style={{ background: 'none', border: `1px solid ${tema.accent}55`, color: tema.accent, padding: '10px 18px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                  🎲 Aleatoria
                </button>
                <button onClick={abrirCrear}
                  style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 18px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                  + Nueva plantilla
                </button>
              </div>
            </div>

            {error && (
              <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '8px', padding: '12px', color: '#e74c3c', marginBottom: '16px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            {cargando ? (
              <div style={{ color: tema.muted, padding: '32px', textAlign: 'center' }}>Cargando...</div>
            ) : plantillas.length === 0 ? (
              <div style={{ color: tema.muted, padding: '48px', textAlign: 'center', background: tema.bgCard, borderRadius: '12px' }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                <p style={{ fontSize: '16px', marginBottom: '6px' }}>No hay plantillas guardadas</p>
                <p style={{ fontSize: '13px' }}>Crea tu primera plantilla para reutilizarla en futuros exámenes</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {plantillas.map(p => (
                  <div key={p.id} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '10px', padding: '16px 20px' }}>
                    <div className="card-row">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          <p style={{ color: tema.text, fontWeight: 600, fontFamily: 'Inter, sans-serif', margin: 0 }}>{p.titulo}</p>
                          {p.categoria && (
                            <span style={{ background: `${tema.accent}15`, color: tema.accent, fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                              {p.categoria}
                            </span>
                          )}
                          <span style={{ background: 'rgba(100,100,100,0.1)', color: tema.muted, fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>
                            {p.subtipo === 'actividad' ? 'Actividad' : 'Examen'}
                          </span>
                        </div>
                        {p.descripcion && <p style={{ color: tema.muted, fontSize: '13px', margin: 0 }}>{p.descripcion}</p>}
                        <p style={{ color: tema.muted, fontSize: '11px', marginTop: '4px' }}>
                          Creada: {new Date(p.fecha_creacion).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <div className="card-actions">
                        <button onClick={() => usarComoExamen(p)}
                          style={{ background: `${tema.accent}22`, border: `1px solid ${tema.accent}44`, color: tema.accent, padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          Usar como examen
                        </button>
                        <button onClick={() => usarComoActividad(p)}
                          style={{ background: `${tema.accent}12`, border: `1px solid ${tema.accent}33`, color: tema.accent, padding: '8px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          Como actividad
                        </button>
                        <button onClick={() => abrirEditar(p.id)} title="Editar"
                          style={{ background: `${tema.accent}12`, border: `1px solid ${tema.accent}33`, color: tema.accent, padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <PencilSquareIcon style={{ width: 17, height: 17 }} />
                        </button>
                        <button onClick={() => eliminar(p.id)} title="Eliminar"
                          style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', color: '#e74c3c', padding: '8px 9px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <TrashIcon style={{ width: 17, height: 17 }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal plantilla aleatoria */}
      {modalAleatorio && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '20px', width: '100%', maxWidth: '600px', marginBottom: '24px', overflow: 'hidden' }}>
            {/* Cabecera */}
            <div style={{ background: `linear-gradient(135deg, ${tema.primary}22, ${tema.accent}12)`, borderBottom: `1px solid ${tema.border}`, padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: tema.text, fontSize: '19px', margin: '0 0 4px', fontWeight: 700 }}>🎲 Plantilla aleatoria</h3>
                <p style={{ color: tema.muted, fontSize: '13px', margin: 0 }}>Selecciona exámenes y se mezclarán sus preguntas</p>
              </div>
              <button onClick={() => setModalAleatorio(false)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer', fontSize: '20px' }}>✕</button>
            </div>

            <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {errorAleatorio && (
                <div style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '10px', padding: '10px 14px', color: '#e74c3c', fontSize: '13px' }}>
                  {errorAleatorio}
                </div>
              )}

              {/* Título */}
              <div>
                <label style={labelStyle}>Título de la plantilla *</label>
                <input style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px', boxSizing: 'border-box' }}
                  value={tituloAleatorio} onChange={e => setTituloAleatorio(e.target.value)} />
              </div>

              {/* Número de preguntas */}
              <div>
                <label style={labelStyle}>Número de preguntas a incluir (dejar vacío = todas)</label>
                <input type="number" min="1" style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px', boxSizing: 'border-box' }}
                  value={numPreguntasAleatorio} onChange={e => setNumPreguntasAleatorio(e.target.value)} placeholder="Ej: 10" />
              </div>

              {/* Lista de evaluaciones */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelStyle}>Selecciona los exámenes/actividades *</label>
                  {evalsDisponibles.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" onClick={() => setEvalsSeleccionadas(new Set(evalsDisponibles.map(e => e.id)))}
                        style={{ background: 'none', border: 'none', color: tema.accent, fontSize: '12px', cursor: 'pointer', padding: 0 }}>
                        Todos
                      </button>
                      <span style={{ color: tema.muted, fontSize: '12px' }}>·</span>
                      <button type="button" onClick={() => setEvalsSeleccionadas(new Set())}
                        style={{ background: 'none', border: 'none', color: tema.muted, fontSize: '12px', cursor: 'pointer', padding: 0 }}>
                        Ninguno
                      </button>
                    </div>
                  )}
                </div>

                {cargandoEvals ? (
                  <div style={{ color: tema.muted, textAlign: 'center', padding: '24px', fontSize: '14px' }}>Cargando exámenes...</div>
                ) : evalsDisponibles.length === 0 ? (
                  <div style={{ color: tema.muted, textAlign: 'center', padding: '24px', fontSize: '14px', background: tema.bg, borderRadius: '10px' }}>
                    No hay exámenes con preguntas disponibles
                  </div>
                ) : (
                  <div style={{ maxHeight: '340px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(
                      evalsDisponibles.reduce((acc, e) => {
                        const cat = e.categoria || 'General';
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(e);
                        return acc;
                      }, {})
                    ).map(([cat, evals]) => {
                      const ids = evals.map(e => e.id);
                      const todosSeleccionados = ids.every(id => evalsSeleccionadas.has(id));
                      return (
                        <div key={cat}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', marginBottom: '4px' }}>
                            <span style={{ color: tema.accent, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{cat}</span>
                            <button type="button" onClick={() => toggleGrupo(ids, todosSeleccionados)}
                              style={{ background: 'none', border: 'none', color: tema.muted, fontSize: '11px', cursor: 'pointer', padding: 0 }}>
                              {todosSeleccionados ? 'Quitar todos' : 'Seleccionar todos'}
                            </button>
                          </div>
                          {evals.map(e => (
                            <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: evalsSeleccionadas.has(e.id) ? `${tema.accent}10` : tema.bg, border: `1px solid ${evalsSeleccionadas.has(e.id) ? tema.accent + '44' : tema.border}`, cursor: 'pointer', marginBottom: '6px', transition: 'all 0.15s' }}>
                              <input type="checkbox" checked={evalsSeleccionadas.has(e.id)} onChange={() => toggleEval(e.id)}
                                style={{ accentColor: tema.accent, width: '16px', height: '16px', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ color: tema.text, fontSize: '13px', fontWeight: 600, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.titulo}</p>
                                <p style={{ color: tema.muted, fontSize: '11px', margin: 0 }}>{e.tipo} · {e.nivel} · {e.num_preguntas} pregunta{e.num_preguntas !== 1 ? 's' : ''}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                {evalsSeleccionadas.size > 0 && (
                  <p style={{ color: tema.accent, fontSize: '12px', marginTop: '8px', fontWeight: 600 }}>
                    {evalsSeleccionadas.size} examen{evalsSeleccionadas.size !== 1 ? 'es' : ''} seleccionado{evalsSeleccionadas.size !== 1 ? 's' : ''} · {evalsDisponibles.filter(e => evalsSeleccionadas.has(e.id)).reduce((s, e) => s + parseInt(e.num_preguntas), 0)} preguntas disponibles
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={crearAleatoria} disabled={creandoAleatorio}
                  style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1, boxShadow: '0 4px 14px rgba(139,111,212,0.35)', opacity: creandoAleatorio ? 0.7 : 1 }}>
                  {creandoAleatorio ? 'Creando...' : '🎲 Crear plantilla aleatoria'}
                </button>
                <button onClick={() => setModalAleatorio(false)}
                  style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '12px 20px', borderRadius: '10px', fontSize: '14px', cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal crear/editar plantilla — wizard 2 pasos */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
          <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '20px', width: '100%', maxWidth: '680px', marginBottom: '24px', overflow: 'hidden' }}>

            {/* Cabecera */}
            <div style={{ background: `linear-gradient(135deg, ${tema.primary}22, ${tema.accent}12)`, borderBottom: `1px solid ${tema.border}`, padding: '22px 28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ color: tema.text, fontSize: '19px', margin: '0 0 4px', fontWeight: 700 }}>
                    {modal === 'editar' ? '✏️ Editar plantilla' : '📋 Nueva plantilla'}
                  </h3>
                  <p style={{ color: tema.muted, fontSize: '13px', margin: 0 }}>
                    Paso {paso} de {esRedaccionForm ? '1' : '2'} — {paso === 1 ? 'Información básica' : 'Preguntas'}
                  </p>
                </div>
                <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer', padding: '4px' }}>
                  <XMarkIcon style={{ width: 22, height: 22 }} />
                </button>
              </div>
              {/* Barra de progreso */}
              <div style={{ display: 'flex', gap: '6px', marginTop: '16px' }}>
                {[1, ...(esRedaccionForm ? [] : [2])].map(n => (
                  <div key={n} style={{ height: '4px', flex: 1, borderRadius: '4px', background: paso >= n ? tema.primary : `${tema.border}`, transition: 'background 0.3s' }} />
                ))}
              </div>
            </div>

            <div style={{ padding: '24px 28px' }}>
              {formError && (
                <div style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: '10px', padding: '10px 14px', color: '#e74c3c', fontSize: '13px', marginBottom: '18px' }}>
                  {formError}
                </div>
              )}

              {/* ── PASO 1: Información básica ── */}
              {paso === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={labelStyle}>Título de la plantilla *</label>
                    <input style={{ ...inputStyle, fontSize: '15px', padding: '12px 16px' }} value={form.titulo}
                      onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                      placeholder="Ej: Listening B2 — Comprensión auditiva" autoFocus />
                  </div>
                  <div>
                    <label style={labelStyle}>Descripción (opcional)</label>
                    <input style={inputStyle} value={form.descripcion}
                      onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                      placeholder="Breve descripción de esta plantilla" />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>Categoría</label>
                      <select style={inputStyle} value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                        {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Tipo</label>
                      <select style={inputStyle} value={form.subtipo} onChange={e => setForm(f => ({ ...f, subtipo: e.target.value }))}>
                        <option value="examen">Examen</option>
                        <option value="actividad">Actividad</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Duración (min)</label>
                      <input type="number" style={inputStyle} value={form.duracion_minutos}
                        onChange={e => setForm(f => ({ ...f, duracion_minutos: e.target.value }))} placeholder="60" />
                    </div>
                  </div>

                  {form.categoria === 'Writing' && (
                    <div style={{ background: `${tema.accent}08`, border: `1px solid ${tema.accent}22`, borderRadius: '10px', padding: '14px 16px' }}>
                      <label style={{ ...labelStyle, color: tema.accent, fontWeight: 600 }}>Formato Writing</label>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                        {[{ v: 'test', label: '📝 Test de preguntas' }, { v: 'redaccion', label: '✍️ Redacción libre' }].map(op => (
                          <button key={op.v} onClick={() => setForm(f => ({ ...f, subtipo_eval: op.v }))}
                            style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `2px solid ${form.subtipo_eval === op.v ? tema.accent : tema.border}`, background: form.subtipo_eval === op.v ? `${tema.accent}18` : 'transparent', color: form.subtipo_eval === op.v ? tema.accent : tema.muted, fontSize: '13px', fontWeight: form.subtipo_eval === op.v ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s' }}>
                            {op.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {form.categoria === 'Listening' && (
                    <div>
                      <label style={labelStyle}>🎧 Link de audio (YouTube)</label>
                      <input style={inputStyle} value={form.link_audio}
                        onChange={e => setForm(f => ({ ...f, link_audio: e.target.value }))} placeholder="https://youtube.com/..." />
                    </div>
                  )}
                  {form.categoria === 'Reading' && (
                    <div>
                      <label style={labelStyle}>📖 Texto de lectura</label>
                      <textarea style={{ ...inputStyle, minHeight: '120px', resize: 'vertical', lineHeight: '1.6' }}
                        value={form.texto_lectura}
                        onChange={e => setForm(f => ({ ...f, texto_lectura: e.target.value }))}
                        placeholder="Pega aquí el texto de lectura..." />
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                    {!esRedaccionForm ? (
                      <button onClick={() => { if (!form.titulo.trim()) { setFormError('El título es requerido'); return; } setFormError(''); setPaso(2); }}
                        style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1, boxShadow: '0 4px 14px rgba(139,111,212,0.35)' }}>
                        Siguiente: Preguntas →
                      </button>
                    ) : (
                      <button onClick={guardar} disabled={guardando}
                        style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1, boxShadow: '0 4px 14px rgba(139,111,212,0.35)' }}>
                        {guardando ? 'Guardando...' : modal === 'editar' ? '✓ Guardar cambios' : '✓ Guardar plantilla'}
                      </button>
                    )}
                    <button onClick={() => setModal(null)}
                      style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '12px 20px', borderRadius: '10px', fontSize: '14px', cursor: 'pointer' }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* ── PASO 2: Preguntas ── */}
              {paso === 2 && !esRedaccionForm && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <p style={{ color: tema.text, fontWeight: 600, margin: '0 0 2px' }}>{form.titulo}</p>
                      <p style={{ color: tema.muted, fontSize: '12px', margin: 0 }}>{form.preguntas.length} pregunta{form.preguntas.length !== 1 ? 's' : ''}</p>
                    </div>
                    <button onClick={addPregunta}
                      style={{ background: `${tema.accent}18`, border: `1px solid ${tema.accent}33`, color: tema.accent, padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <PlusIcon style={{ width: 15, height: 15 }} /> Añadir pregunta
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>
                    {form.preguntas.map((preg, pi) => (
                      <div key={pi} style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ background: `${tema.accent}20`, color: tema.accent, fontSize: '12px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px' }}>
                            #{pi + 1}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <select style={{ ...inputStyle, width: 'auto', padding: '6px 10px', fontSize: '12px' }} value={preg.tipo_pregunta} onChange={e => updatePregunta(pi, 'tipo_pregunta', e.target.value)}>
                              <option value="multiple">Múltiple opción</option>
                              <option value="verdadero_falso">Verdadero / Falso</option>
                              <option value="rellenar_huecos">Rellenar huecos</option>
                            </select>
                            {form.preguntas.length > 1 && (
                              <button onClick={() => removePregunta(pi)} style={{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', color: '#e74c3c', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                Eliminar
                              </button>
                            )}
                          </div>
                        </div>

                        <input style={{ ...inputStyle, marginBottom: '10px', fontWeight: 500 }} value={preg.texto}
                          onChange={e => updatePregunta(pi, 'texto', e.target.value)}
                          placeholder={preg.tipo_pregunta === 'rellenar_huecos' ? 'Usa ___ para marcar cada hueco. Ej: The cat is ___' : 'Escribe la pregunta aquí...'} />

                        {preg.tipo_pregunta === 'multiple' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {preg.opciones.map((op, oi) => (
                              <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: preg.respuesta_correcta === op && op ? '#27ae60' : tema.border, flexShrink: 0 }} />
                                <input style={{ ...inputStyle, flex: 1, padding: '8px 12px' }} value={op}
                                  onChange={e => updateOpcion(pi, oi, e.target.value)} placeholder={`Opción ${oi + 1}`} />
                              </div>
                            ))}
                            <div style={{ marginTop: '4px' }}>
                              <label style={labelStyle}>✓ Opción correcta</label>
                              <select style={{ ...inputStyle, background: 'rgba(39,174,96,0.06)', border: '1px solid rgba(39,174,96,0.3)', color: '#27ae60' }}
                                value={preg.respuesta_correcta} onChange={e => updatePregunta(pi, 'respuesta_correcta', e.target.value)}>
                                <option value="">Selecciona la respuesta correcta...</option>
                                {preg.opciones.filter(Boolean).map((op, oi) => <option key={oi} value={op}>{op}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                        {preg.tipo_pregunta === 'verdadero_falso' && (
                          <div style={{ display: 'flex', gap: '10px' }}>
                            {['Verdadero', 'Falso'].map(v => (
                              <button key={v} onClick={() => updatePregunta(pi, 'respuesta_correcta', v)}
                                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `2px solid ${preg.respuesta_correcta === v ? '#27ae60' : tema.border}`, background: preg.respuesta_correcta === v ? 'rgba(39,174,96,0.1)' : 'transparent', color: preg.respuesta_correcta === v ? '#27ae60' : tema.muted, fontWeight: preg.respuesta_correcta === v ? 700 : 400, cursor: 'pointer', fontSize: '14px' }}>
                                {v === 'Verdadero' ? '✓ Verdadero' : '✗ Falso'}
                              </button>
                            ))}
                          </div>
                        )}
                        {preg.tipo_pregunta === 'rellenar_huecos' && (
                          <div>
                            <label style={labelStyle}>Respuestas para los huecos (en orden, separadas por coma)</label>
                            <input style={{ ...inputStyle, background: 'rgba(39,174,96,0.06)', border: '1px solid rgba(39,174,96,0.3)' }}
                              value={Array.isArray(preg.respuesta_correcta) ? preg.respuesta_correcta.join(', ') : preg.respuesta_correcta}
                              onChange={e => updatePregunta(pi, 'respuesta_correcta', e.target.value)}
                              placeholder="Ej: sitting, table" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                    <button onClick={() => { setPaso(1); setFormError(''); }}
                      style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '12px 20px', borderRadius: '10px', fontSize: '14px', cursor: 'pointer' }}>
                      ← Volver
                    </button>
                    <button onClick={guardar} disabled={guardando}
                      style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', flex: 1, boxShadow: '0 4px 14px rgba(139,111,212,0.35)' }}>
                      {guardando ? 'Guardando...' : modal === 'editar' ? '✓ Guardar cambios' : '✓ Guardar plantilla'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
